import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Grid, TextField } from '@material-ui/core'
import { SecaoGenerica } from 'components'
import { Dente } from './Components/Dente'
import { useExameNecrodontoscopicoService } from 'hooks/useExameNecrodontoscopico'
import { useStyles } from './FormMapaOdontologico.styles'

export const FormMapaOdontologico = (props) => {
  const classes = useStyles()
  const { idExameNecrodontoscopico, setProcessandoDados, dentes, setDentes, setEditar } = props
  const exameNecrodontoscopicoService = useExameNecrodontoscopicoService()

  const fetchDentes = useCallback(async () => {
    setProcessandoDados(true)
    try {
      const response = await exameNecrodontoscopicoService.listarDentes(idExameNecrodontoscopico)
      if (response?.length) {
        setDentes(response)
        setEditar(true)
      }
    } catch (error) {
      console.error('Failed to fetch teeth data', error)
    } finally {
      setProcessandoDados(false)
    }
  }, [idExameNecrodontoscopico, setDentes, setEditar, setProcessandoDados])

  useEffect(() => {
    fetchDentes()
  }, [fetchDentes])

  const getDadosDente = useCallback(
    (identificador) => {
      return dentes.find((dente) => dente.identificador === identificador) || {
        identificador,
        ausente: false,
        faceDistal: false,
        faceVestibular: false,
        faceMesial: false,
        faceLingual: false,
        observacoes: null,
      }
    },
    [dentes]
  )

  const setDente = useCallback((identificador, dados) => {
    setDentes((prevState) =>
      prevState.map((dente) => {
        if (dente.identificador === identificador) {
          return dados
        }
        return dente
      })
    )
  }, [setDentes])

  const handleChangeObservacoes = (identificadores, value) => {
    setDentes((prevState) =>
      prevState.map((dente) => {
        if (identificadores.includes(dente.identificador)) {
          return {
            ...dente,
            observacoes: value,
          }
        }
        return dente
      })
    )
  }

  const isEnabled = (identificadores) => {
    return identificadores.some((identificador) =>
      dentes.some(
        (dente) =>
          dente.identificador === identificador &&
          !dente.ausente &&
          (dente.faceDistal || dente.faceVestibular || dente.faceMesial || dente.faceLingual)
      )
    )
  }

  return (
    <>
      <SecaoGenerica titulo="Odontograma - Mapa Anatômico Dentário">
        <Grid item xs={12}>
          <div id="mapaOdonto" className={classes.mapaContainer}>
            {/* Fileira Superior Permanente */}
            <div className={classes.toothRow}>
              {['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'].map((id) => (
                <Dente key={id} identificador={id} dente={getDadosDente(id)} setDente={setDente} />
              ))}
            </div>

            {/* Fileira Superior Decídua */}
            <div className={classes.toothRow}>
              {['55', '54', '53', '52', '51', '61', '62', '63', '64', '65'].map((id) => (
                <Dente key={id} identificador={id} dente={getDadosDente(id)} setDente={setDente} />
              ))}
            </div>

            {/* Fileira Inferior Decídua */}
            <div className={classes.toothRow}>
              {['85', '84', '83', '82', '81', '71', '72', '73', '74', '75'].map((id) => (
                <Dente key={id} identificador={id} dente={getDadosDente(id)} setDente={setDente} />
              ))}
            </div>

            {/* Fileira Inferior Permanente */}
            <div className={classes.toothRowBottom}>
              {['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38'].map((id) => (
                <Dente key={id} identificador={id} dente={getDadosDente(id)} setDente={setDente} />
              ))}
            </div>
          </div>
        </Grid>
      </SecaoGenerica>

      <Grid container spacing={3}>
        {/* Quadrante: Superiores Direitos */}
        <Grid item xs={12} md={6}>
          <SecaoGenerica titulo="Dentes Superiores Direitos" spacing={2}>
            <Grid item xs={12}>
              <div className={classes.formDentes}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="51 / 11"
                  name="51 / 11"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('11').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['51', '11'], e.target.value)}
                  disabled={!isEnabled(['51', '11'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="52 / 12"
                  name="52 / 12"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('12').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['52', '12'], e.target.value)}
                  disabled={!isEnabled(['52', '12'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="53 / 13"
                  name="53 / 13"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('13').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['53', '13'], e.target.value)}
                  disabled={!isEnabled(['53', '13'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="54 / 14"
                  name="54 / 14"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('14').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['54', '14'], e.target.value)}
                  disabled={!isEnabled(['54', '14'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="55 / 15"
                  name="55 / 15"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('15').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['55', '15'], e.target.value)}
                  disabled={!isEnabled(['55', '15'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="16"
                  name="16"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('16').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['16'], e.target.value)}
                  disabled={!isEnabled(['16'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="17"
                  name="17"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('17').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['17'], e.target.value)}
                  disabled={!isEnabled(['17'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="18"
                  name="18"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('18').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['18'], e.target.value)}
                  disabled={!isEnabled(['18'])}
                />
              </div>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* Quadrante: Superiores Esquerdos */}
        <Grid item xs={12} md={6}>
          <SecaoGenerica titulo="Dentes Superiores Esquerdos" spacing={2}>
            <Grid item xs={12}>
              <div className={classes.formDentes}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="61 / 21"
                  name="61 / 21"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('21').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['61', '21'], e.target.value)}
                  disabled={!isEnabled(['61', '21'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="62 / 22"
                  name="62 / 22"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('22').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['62', '22'], e.target.value)}
                  disabled={!isEnabled(['62', '22'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="63 / 23"
                  name="63 / 23"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('23').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['63', '23'], e.target.value)}
                  disabled={!isEnabled(['63', '23'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="64 / 24"
                  name="64 / 24"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('24').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['64', '24'], e.target.value)}
                  disabled={!isEnabled(['64', '24'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="65 / 25"
                  name="65 / 25"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('25').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['65', '25'], e.target.value)}
                  disabled={!isEnabled(['65', '25'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="26"
                  name="26"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('26').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['26'], e.target.value)}
                  disabled={!isEnabled(['26'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="27"
                  name="27"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('27').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['27'], e.target.value)}
                  disabled={!isEnabled(['27'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="28"
                  name="28"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('28').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['28'], e.target.value)}
                  disabled={!isEnabled(['28'])}
                />
              </div>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* Quadrante: Inferiores Esquerdos */}
        <Grid item xs={12} md={6}>
          <SecaoGenerica titulo="Dentes Inferiores Esquerdos" spacing={2}>
            <Grid item xs={12}>
              <div className={classes.formDentes}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="71 / 31"
                  name="71 / 31"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('31').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['71', '31'], e.target.value)}
                  disabled={!isEnabled(['71', '31'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="72 / 32"
                  name="72 / 32"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('32').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['72', '32'], e.target.value)}
                  disabled={!isEnabled(['72', '32'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="73 / 33"
                  name="73 / 33"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('33').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['73', '33'], e.target.value)}
                  disabled={!isEnabled(['73', '33'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="74 / 34"
                  name="74 / 34"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('34').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['74', '34'], e.target.value)}
                  disabled={!isEnabled(['74', '34'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="75 / 35"
                  name="75 / 35"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('35').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['75', '35'], e.target.value)}
                  disabled={!isEnabled(['75', '35'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="36"
                  name="36"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('36').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['36'], e.target.value)}
                  disabled={!isEnabled(['36'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="37"
                  name="37"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('37').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['37'], e.target.value)}
                  disabled={!isEnabled(['37'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="38"
                  name="38"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('38').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['38'], e.target.value)}
                  disabled={!isEnabled(['38'])}
                />
              </div>
            </Grid>
          </SecaoGenerica>
        </Grid>

        {/* Quadrante: Inferiores Direitos */}
        <Grid item xs={12} md={6}>
          <SecaoGenerica titulo="Dentes Inferiores Direitos" spacing={2}>
            <Grid item xs={12}>
              <div className={classes.formDentes}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="81 / 41"
                  name="81 / 41"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('41').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['81', '41'], e.target.value)}
                  disabled={!isEnabled(['81', '41'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="82 / 42"
                  name="82 / 42"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('42').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['82', '42'], e.target.value)}
                  disabled={!isEnabled(['82', '42'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="83 / 43"
                  name="83 / 43"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('43').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['83', '43'], e.target.value)}
                  disabled={!isEnabled(['83', '43'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="84 / 44"
                  name="84 / 44"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('44').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['84', '44'], e.target.value)}
                  disabled={!isEnabled(['84', '44'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="85 / 45"
                  name="85 / 45"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('45').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['85', '45'], e.target.value)}
                  disabled={!isEnabled(['85', '45'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="46"
                  name="46"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('46').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['46'], e.target.value)}
                  disabled={!isEnabled(['46'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="47"
                  name="47"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('47').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['47'], e.target.value)}
                  disabled={!isEnabled(['47'])}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="48"
                  name="48"
                  InputLabelProps={{ shrink: true }}
                  value={getDadosDente('48').observacoes || ''}
                  onChange={(e) => handleChangeObservacoes(['48'], e.target.value)}
                  disabled={!isEnabled(['48'])}
                />
              </div>
            </Grid>
          </SecaoGenerica>
        </Grid>
      </Grid>
    </>
  )
}

FormMapaOdontologico.propTypes = {
  idExameNecrodontoscopico: PropTypes.number.isRequired,
  setProcessandoDados: PropTypes.func.isRequired,
  dentes: PropTypes.array.isRequired,
  setDentes: PropTypes.func.isRequired,
  setEditar: PropTypes.func.isRequired,
}
